/**
 * threat_indicator — WHERE a blow wound up at you is coming from (sweep 2026-10-05). Pure: no DOM, no three.
 *
 *   THE BEARING  absolute player → attacker, minus the CAMERA's heading (the waypoint HUD's `relativeBearing`: compass
 *                heading, 0 = ahead, +90° = on your right, (−π, π]). The rig's yaw is NOT the camera when you orbit.
 *   THE RAMP     a windup lasts [startMs, impactMs]; urgency climbs 0 → 1 (eased, so the last third is where it
 *                brightens) and the arc is gone `GRACE_MS` after the impact (the row's own delete ends it sooner).
 *   THE RING     the arc sits on the ellipse inset `margin` from the screen edge at the bearing; its rotation is the
 *                bearing, so the chevron points at the attacker.
 *   THE GATE     never in a headset, in space, on the placement map, under the loading gate or in a cinematic.
 */
import { relativeBearing, wrapPi } from './waypoint_hud.js';

export { relativeBearing, wrapPi };

export type ThreatSource = 'npc' | 'pvp' | 'hunter';
export interface Threat { id: string; source: ThreatSource; x: number; z: number; startMs: number; impactMs: number }

/** The arc lingers this long past the impact if no resolve arrives (a missed delete must not leave it forever). */
export const GRACE_MS = 450;
/** At most this many arcs at once (the soonest blows). */
export const MAX_ARCS = 3;
/** The dimmest and brightest an arc gets across the windup. */
export const MIN_ALPHA = 0.3;
export const MAX_ALPHA = 1;

/** 0 at the start of the windup → 1 at the impact; a zero / negative / non-finite windup reads as 1 (already landing). */
export function urgency(nowMs: number, startMs: number, impactMs: number): number {
  const span = impactMs - startMs;
  if (!Number.isFinite(span) || !Number.isFinite(nowMs) || span <= 0) return 1;
  const t = Math.max(0, Math.min(1, (nowMs - startMs) / span));
  return t * t * (3 - 2 * t); // smoothstep: slow to climb, quick at the end
}

/** The arc's opacity for an urgency. */
export function alphaFor(u: number): number { return MIN_ALPHA + (MAX_ALPHA - MIN_ALPHA) * Math.max(0, Math.min(1, u)); }

export function expired(nowMs: number, t: Threat): boolean { return nowMs > t.impactMs + GRACE_MS; }

/** A pulse multiplier on the opacity: 1 with no motion allowed; otherwise 1 − amp·(½ + ½ sin), faster as it nears. */
export function pulse(nowMs: number, u: number, motionScale: number): number {
  const s = Math.max(0, Math.min(1, motionScale));
  if (s <= 0) return 1;
  const hz = 1.5 + 4 * Math.max(0, Math.min(1, u));
  return 1 - 0.3 * s * (0.5 + 0.5 * Math.sin((nowMs / 1000) * hz * Math.PI * 2));
}

/** The soonest-landing live threats, at most `max`. */
export function liveThreats(list: readonly Threat[], nowMs: number, max = MAX_ARCS): Threat[] {
  return list.filter((t) => !expired(nowMs, t)).sort((a, b) => a.impactMs - b.impactMs).slice(0, max);
}

/** The arc's anchor on the screen ellipse for a relative bearing (0 = top-centre, + clockwise), and its rotation (deg). */
export function ringPoint(rel: number, W: number, H: number, margin: number): { x: number; y: number; deg: number } {
  const r = Number.isFinite(rel) ? rel : 0;
  const rx = Math.max(1, W / 2 - margin), ry = Math.max(1, H / 2 - margin);
  return { x: W / 2 + Math.sin(r) * rx, y: H / 2 - Math.cos(r) * ry, deg: (r * 180) / Math.PI };
}

/** A readable side for the screen reader / lever: "ahead", "right", "behind left" … */
export function sideWords(rel: number): string {
  const a = Math.abs((rel * 180) / Math.PI);
  if (a <= 22) return 'ahead';
  const side = rel > 0 ? 'right' : 'left';
  if (a <= 67) return `ahead ${side}`;
  if (a <= 112) return side;
  if (a < 158) return `behind ${side}`;
  return 'behind';
}

export interface ThreatGate { xrPresenting: boolean; spaceFlight: boolean; placementView: boolean; loadingGate: boolean; cinematic: boolean; inWorld: boolean }
/** Why the indicator is hidden ('' = it may show). */
export function gateReason(g: ThreatGate): '' | 'xr' | 'space' | 'placement-map' | 'loading' | 'cinematic' | 'no-world' {
  if (g.xrPresenting) return 'xr';
  if (g.spaceFlight) return 'space';
  if (g.placementView) return 'placement-map';
  if (g.loadingGate) return 'loading';
  if (g.cinematic) return 'cinematic';
  if (!g.inWorld) return 'no-world';
  return '';
}

/** The bearing of a threat from the player under a camera heading. */
export function threatBearing(camHeading: number, px: number, pz: number, t: Pick<Threat, 'x' | 'z'>): number {
  if (!Number.isFinite(t.x) || !Number.isFinite(t.z)) return 0;
  if (Math.hypot(t.x - px, t.z - pz) < 1e-6) return 0; // on top of you: no direction — ahead
  return relativeBearing(camHeading, px, pz, t.x, t.z);
}
